import React, { useState, useEffect, useRef } from 'react';
import { useConsultation } from '../contexts/ConsultationContext';
import { RichTextEditor } from './RichTextEditor';
import { PrescriptionSection } from './PrescriptionSection';
import { InvestigationsSection } from './InvestigationsSection';
import ClinicalAICopilot from './ClinicalAICopilot';
import VisitHistoryDrawer from './clinical-history/VisitHistoryDrawer';
import MedicineHistoryDrawer from './clinical-history/MedicineHistoryDrawer';
import InvestigationHistoryDrawer from './clinical-history/InvestigationHistoryDrawer';
import { AdmissionRequestModal } from './AdmissionRequestModal';
import { ReviewDatePicker } from './ReviewDatePicker';
import ReferralSection from './ReferralSection';
import { PatientSearchModal } from './PatientSearchModal';
import { AISuggestionsPanel } from './AISuggestionsPanel';
import { TemplateSelector } from './TemplateSelector';
import { PrescriptionPrint } from './PrescriptionPrint';
import { PastVisitDrawer } from './PastVisitDrawer';
import FullVisitDictationButton from './FullVisitDictationButton';
import VoiceMicButton from './VoiceMicButton';
import { WORKFLOW_GROUPS } from './consultationWorkflow';
import axios from 'axios';
import { toast } from 'sonner';
import { splitSymptoms, stripHtml } from '../utils/splitSymptoms';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '../components/ui/tooltip';
import { 
  Save, CheckCircle, AlertCircle, Loader2, FileText, ClipboardList, History,
  Stethoscope, HelpCircle, FileCheck, Pill, TestTube, BedDouble, StickyNote,
  CalendarDays, User, Phone, Calendar, Hash, PanelRightClose, PanelRightOpen,
  Clock, ArrowLeft, CreditCard, Activity, UserPlus, UserCheck, Shield, Printer,
  ChevronUp, ChevronDown, Eye, ShieldCheck, X as XIcon, Microscope, Send
} from 'lucide-react';

import { formatDateIST, formatDateTimeIST } from '../utils/date';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SECTION_ICONS = {
  general_info: User, chief_complaints: ClipboardList, past_history: History,
  tumor_board: Microscope,
  clinical_examination: Stethoscope, provisional_diagnosis: HelpCircle,
  final_diagnosis: FileCheck, treatment_plan: FileText, prescription: Pill,
  investigations: TestTube, admission_request: BedDouble, visit_notes: StickyNote,
  review_date: CalendarDays, referral: Send, visit_history: Clock
};

const TAB_LABELS = {
  general_info: 'General Info', chief_complaints: 'Chief Complaints',
  past_history: 'Past History', tumor_board: 'Tumor Board',
  clinical_examination: 'Clinical Examination',
  provisional_diagnosis: 'Provisional Diagnosis', final_diagnosis: 'Final Diagnosis',
  treatment_plan: 'Treatment Plan', prescription: 'Prescription',
  investigations: 'Investigations', admission_request: 'Admission Request',
  visit_notes: 'Visit Notes', review_date: 'Follow-up',
  referral: 'Referral', visit_history: 'Visit History'
};

export function ConsultationDashboard({ selectedPatient }) {
  const {
    consultation, activeSection, setActiveSection, saveStatus, focusMode, setFocusMode,
    SECTIONS, saveDraft, saveNow, completeConsultation, retryPharmacyPosting, getCompletionPercentage,
    applyTemplate, patients, onBack
  } = useConsultation();

  const [showPatientSearch, setShowPatientSearch] = useState(false);
  const [showAdmission, setShowAdmission] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showPrint, setShowPrint] = useState(false);
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);

  // Carry-forward banner state.
  //
  // The previous-visit prefill flow can stamp narrative sections with
  // "Carried forward from previous visit:" markers + add medicines/tests
  // tagged via the previousVisit copy. When a doctor reopens a
  // consultation that was populated in an EARLIER session, that data is
  // still there — "Start Fresh" only closes the modal, it never strips
  // existing carry-forward content. This banner gives the doctor a
  // one-click escape: "Clear All" wipes everything tagged as
  // carry-forward; "Keep" dismisses the banner for the session.
  //
  // `dismissedKey` is consultation-scoped so re-opening the same
  // consultation in a different session re-surfaces the offer.
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [clearingCarryForward, setClearingCarryForward] = useState(false);

  useEffect(() => {
    if (!consultation?.id) return;
    try {
      const k = `carry-banner-dismissed-${consultation.id}`;
      setBannerDismissed(window.sessionStorage.getItem(k) === '1');
    } catch (_) {
      setBannerDismissed(false);
    }
  }, [consultation?.id]);

  const NARRATIVE_KEYS = ['chief_complaints', 'past_history', 'tumor_board',
    'clinical_examination', 'provisional_diagnosis', 'final_diagnosis',
    'treatment_plan', 'visit_notes'];

  // Inspect every narrative section + any rx/test row for the
  // carry-forward marker. We track each section / item so "Clear All"
  // can do a surgical strip rather than a full wipe.
  const carryForwardSummary = React.useMemo(() => {
    const sections = consultation?.sections || {};
    const taintedNarratives = [];
    for (const key of NARRATIVE_KEYS) {
      const html = (sections[key] || {}).content || '';
      if (typeof html === 'string' && html.includes('Carried forward from previous visit')) {
        taintedNarratives.push(key);
      }
    }
    const medItems = (sections.prescription?.items || []);
    const taintedMeds = medItems.filter((m) =>
      m && ((m.from_previous_visit === true) || (m.carried_forward === true) ||
            (typeof m.remarks === 'string' && m.remarks.toLowerCase().includes('carried forward')))
    );
    return {
      narratives: taintedNarratives,
      meds: taintedMeds,
      total: taintedNarratives.length + (taintedMeds.length > 0 ? 1 : 0),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consultation?.sections]);

  const handleClearCarryForward = async () => {
    if (clearingCarryForward) return;
    setClearingCarryForward(true);
    try {
      const sections = consultation?.sections || {};
      const calls = [];

      // Strip the "Carried forward..." block from each tainted narrative.
      // The block is everything from the marker line onwards — the
      // doctor's own typing (which lives ABOVE the marker) stays. We
      // also strip a trailing <hr/> if present so the section doesn't
      // end with a bare separator.
      for (const key of carryForwardSummary.narratives) {
        const html = (sections[key] || {}).content || '';
        // Match the most common shapes the prefill writes:
        //   <hr/><p><em>Carried forward from previous visit:</em></p>...
        //   <p><em>Carried forward from previous visit:</em></p>...
        const stripped = html
          .replace(/<hr\s*\/?>\s*<p>\s*<em>\s*Carried forward from previous visit[\s\S]*$/i, '')
          .replace(/<p>\s*<em>\s*Carried forward from previous visit[\s\S]*$/i, '')
          .trim();
        calls.push(saveDraft(key, stripped));
      }

      // Drop carry-forward medicines + investigations.
      if (carryForwardSummary.meds.length > 0) {
        const keep = (sections.prescription?.items || []).filter((m) =>
          !((m.from_previous_visit === true) || (m.carried_forward === true) ||
            (typeof m.remarks === 'string' && m.remarks.toLowerCase().includes('carried forward')))
        );
        calls.push(saveDraft('prescription', keep));
      }

      await Promise.all(calls);
      try { await saveNow(); } catch (_) {}
      toast.success('Cleared previously carried-forward content');
      // Dismiss the banner — even if some content somehow survived, the
      // user has expressed they don't want to see it again this session.
      try { window.sessionStorage.setItem(`carry-banner-dismissed-${consultation.id}`, '1'); } catch (_) {}
      setBannerDismissed(true);
    } catch (e) {
      console.error('Clear carry-forward failed:', e);
      toast.error('Could not clear carry-forward. Try again or remove sections manually.');
    } finally {
      setClearingCarryForward(false);
    }
  };

  const dismissBanner = () => {
    try { window.sessionStorage.setItem(`carry-banner-dismissed-${consultation?.id}`, '1'); } catch (_) {}
    setBannerDismissed(true);
  };

  // Full-visit voice dictation — distributes the AI-parsed payload across
  // every section the doctor approves. Narrative sections go through the
  // standard saveDraft pipe; rx + tests merge with whatever is already
  // there (dedupe by name) so a half-typed prescription isn't clobbered.
  const handleFullVisitApply = async (payload) => {
    const { sectionsToSave = {}, medicines = [], tests = [], transcript = '' } = payload || {};
    try {
      const currentSections = consultation?.sections || {};
      const calls = [];

      // Narrative sections — append if existing content present, replace
      // if empty. Same posture as the previous-visit prefill carry-forward.
      // Idempotency: skip the append when the new HTML's plain-text body
      // is already embedded in the current content. Stops accidental
      // double-clicks from bloating chief_complaints / diagnosis fields
      // until TipTap can no longer render them.
      const stripTags = (html) => String(html || '').replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
      for (const [key, html] of Object.entries(sectionsToSave)) {
        if (!html) continue;
        const existing = (currentSections[key] || {}).content || '';
        const newPlain = stripTags(html);
        if (newPlain && stripTags(existing).includes(newPlain)) {
          continue;
        }
        const next = existing
          ? `${existing}<hr/><p><em>Added by voice dictation:</em></p>${html}`
          : html;
        calls.push(saveDraft(key, next));
      }

      // Prescription — merge with current rx by medicine_name (case-insensitive).
      if (Array.isArray(medicines) && medicines.length > 0) {
        const currentRx = ((currentSections.prescription || {}).items) || [];
        const have = new Set(currentRx.map((m) => (m.medicine_name || '').trim().toLowerCase()).filter(Boolean));
        const toAdd = medicines
          .filter((m) => m.medicine_name && !have.has(m.medicine_name.trim().toLowerCase()))
          .map((m) => {
            // Mirror PrescriptionSection.handleVoiceConfirm normalisation.
            const f = String(m.frequency || 'BD').toUpperCase().replace(/[^A-Z]/g, '');
            const valid = ['OD','BD','TDS','QID','HS','SOS','STAT','WEEKLY','ALT_DAY'];
            const freq = valid.includes(f) ? f : 'BD';
            let intake = 'after_food';
            const intRaw = String(m.intake || '').toLowerCase();
            if (intRaw.includes('before')) intake = 'before_food';
            else if (intRaw.includes('with')) intake = 'with_food';
            return {
              id: Date.now() + Math.random(),
              medicine_name: m.medicine_name,
              dosage: m.dosage || '',
              frequency: freq,
              intake,
              duration: m.duration || '5 days',
              quantity: m.quantity || '',
              remarks: m.remarks || '',
              timing: undefined,
              timing_doses: {},
            };
          });
        if (toAdd.length > 0) {
          calls.push(saveDraft('prescription', [...currentRx, ...toAdd]));
        }
      }

      // Tests — merge by name. Investigations are stored as PLAIN STRINGS
      // (not {name} objects) — that's what InvestigationsSection /
      // PastVisitDrawer / PrescriptionPrint all render. Saving objects
      // here previously caused React #31 ("Objects are not valid as a
      // React child") on the consultation screen.
      if (Array.isArray(tests) && tests.length > 0) {
        const currentTests = ((currentSections.investigations || {}).items) || [];
        const haveNorm = currentTests
          .map((t) => (typeof t === 'string' ? t : (t && t.name) || ''))
          .filter(Boolean)
          .map((s) => s.trim().toLowerCase());
        const have = new Set(haveNorm);
        const toAdd = tests
          .map((t) => (typeof t === 'string' ? t : (t.name || t.test_name || '')))
          .filter((name) => name && !have.has(name.trim().toLowerCase()));
        if (toAdd.length > 0) {
          // Coerce any existing object entries to strings so the saved
          // shape is uniformly strings post-write.
          const cleanCurrent = currentTests
            .map((t) => (typeof t === 'string' ? t : (t && t.name) || ''))
            .filter(Boolean);
          calls.push(saveDraft('investigations', [...cleanCurrent, ...toAdd]));
        }
      }

      await Promise.all(calls);
      const total = Object.keys(sectionsToSave).length + (medicines.length > 0 ? 1 : 0) + (tests.length > 0 ? 1 : 0);
      toast.success(`Voice visit applied · ${total} section${total !== 1 ? 's' : ''} updated`);
      // Force a save flush so the new content is durable before the doctor
      // navigates away (saveDraft is debounced by 5s).
      try { await saveNow(); } catch (_) {}
      console.debug('[FullVisit] applied transcript chars:', (transcript || '').length);
    } catch (e) {
      console.error('Full visit apply failed:', e);
      toast.error('Could not apply some sections. Please retry or save manually.');
    }
  };

  // Last-visit summary (any prior visit) — surfaced in the consultation footer.
  // Drives two pills: "Last visit by you" and "Last visit (any doctor)" plus
  // a passive "X prior visits" count badge.
  const [lastVisit, setLastVisit] = useState({ by_you: null, by_any: null, total_visits: 0, total_visits_by_you: 0 });
  const [pastVisitId, setPastVisitId] = useState(null);
  const [pastVisitTitle, setPastVisitTitle] = useState('');

  useEffect(() => {
    const pid = consultation?.patient_id || selectedPatient?.patient_id;
    if (!pid) return;
    let cancelled = false;
    const params = {};
    // Exclude both the open consultation AND the current appointment so the
    // backend (which now consults the external EMR for "by_any") doesn't
    // return THIS appointment as its own "previous" visit.
    if (consultation?.id) params.exclude_consultation_id = consultation.id;
    const cur_appt = consultation?.appointment_id || selectedPatient?.appointment_id;
    if (cur_appt) params.exclude_appointment_id = cur_appt;
    axios.get(`${API_URL}/api/patients/${pid}/last-visit`, { params, withCredentials: true })
      .then(({ data }) => { if (!cancelled) setLastVisit(data || { by_you: null, by_any: null, total_visits: 0, total_visits_by_you: 0 }); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [consultation?.patient_id, consultation?.id, consultation?.appointment_id, selectedPatient?.patient_id, selectedPatient?.appointment_id]);
  const [patientDetails, setPatientDetails] = useState(null);
  const [showAIPanel, setShowAIPanel] = useState(false);
  const [recentVisit, setRecentVisit] = useState(null);
  const [symptomTags, setSymptomTags] = useState([]);
  const [collapsedSections, setCollapsedSections] = useState({});
  const [showVisitHistory, setShowVisitHistory] = useState(false);
  const [showMedicineHistory, setShowMedicineHistory] = useState(false);
  const [showInvestigationHistory, setShowInvestigationHistory] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [editingCompleted, setEditingCompleted] = useState(false);
  const [showPharmacyOrder, setShowPharmacyOrder] = useState(false);
  const [showInvestigationsOrder, setShowInvestigationsOrder] = useState(false);
  const [historyInitialTab, setHistoryInitialTab] = useState(null);
  const sectionRefs = useRef({});
  const scrollContainerRef = useRef(null);

  // Whether the visit is completed and NOT in "Edit" mode => read-only
  const isReadOnly = consultation?.status === 'completed' && !editingCompleted;

  // Reset edit mode whenever consultation id changes
  useEffect(() => { setEditingCompleted(false); }, [consultation?.id]);

  // Preview & Print click — flush any pending edits, then ask to complete
  // the visit (unless it's already completed). On confirm, complete first and
  // then open the print modal; on decline, just open the print modal.
  const handlePreviewPrintClick = async () => {
    try { await saveNow(); } catch (_) {}
    if (consultation?.status === 'completed') {
      setShowPrint(true);
    } else {
      setShowCompleteConfirm(true);
    }
  };

  const handleConfirmCompleteThenPrint = async () => {
    setShowCompleteConfirm(false);
    await handleComplete();
    setShowPrint(true);
  };

  const handleSkipCompleteAndPrint = () => {
    setShowCompleteConfirm(false);
    setShowPrint(true);
  };

  // Handle Complete Visit -> post to pharmacy and surface result
  const handleComplete = async () => {
    if (completing) return;
    setCompleting(true);
    const result = await completeConsultation();
    setCompleting(false);
    if (!result?.success) {
      toast.error('Failed to complete visit', { description: result?.error || 'Please try again.' });
      return;
    }
    // If this consultation was opened from the reception queue widget,
    // flip the queue entry from 'in_consult' → 'completed' so reception
    // sees the patient cleared from the waiting list. Fire-and-forget.
    const qid = selectedPatient?.queue_entry_id;
    if (qid) {
      axios.post(
        `${API_URL}/api/clinic-ops/queue/${qid}/complete`,
        {},
        { withCredentials: true }
      ).catch(() => { /* silent — queue status is observability, not critical path */ });
    }
    if (result.pharmacyPosted || result.investigationsPosted) {
      const parts = [];
      if (result.pharmacyPosted) parts.push(`Rx #${result.pharmacyOrderNumber}`);
      if (result.investigationsPosted) parts.push(`Inv #${result.investigationsOrderNumber}`);
      toast.success('Visit completed', { description: parts.join(' · ') });
    } else if (result.pharmacyError || result.investigationsError) {
      toast.warning('Visit completed with issues', {
        description: [result.pharmacyError, result.investigationsError].filter(Boolean).join(' / '),
        action: { label: 'Retry Rx', onClick: async () => {
          const r = await retryPharmacyPosting();
          if (r?.pharmacyPosted) toast.success('Prescription posted', { description: r.pharmacyOrderNumber ? `Pharmacy Order #${r.pharmacyOrderNumber}` : '' });
          else toast.error('Pharmacy posting failed again', { description: r?.pharmacyError || 'Try again later.' });
        } },
      });
    } else {
      toast.success('Visit completed');
    }
  };

  // Get patient details
  useEffect(() => {
    if (consultation?.patient_id && patients) {
      const patient = patients.find(p => p.id === consultation.patient_id);
      setPatientDetails(patient);
    }
    if (selectedPatient) {
      setPatientDetails({
        name: selectedPatient.patient_name,
        medical_record_number: selectedPatient.mrn,
        phone: selectedPatient.phone,
        age: selectedPatient.age || '',
        gender: selectedPatient.gender || ''
      });
    }
  }, [consultation, patients, selectedPatient]);

  // Load most recent visit info
  useEffect(() => {
    const loadRecentVisit = async () => {
      const patientId = selectedPatient?.patient_id || consultation?.patient_id;
      if (!patientId) return;
      try {
        const { data } = await axios.get(`${API_URL}/api/appointments/patient/${patientId}/recent`, { withCredentials: true });
        if (data) setRecentVisit(data);
      } catch (e) { /* ignore */ }
    };
    loadRecentVisit();
  }, [selectedPatient, consultation?.patient_id]);

  // Fetch vitals from external DB and pre-populate general_info
  useEffect(() => {
    const fetchVitals = async () => {
      const apptId = selectedPatient?.appointment_id;
      if (!apptId || !consultation?.id) return;
      // Only auto-fill if general_info is empty/not_started
      const gi = consultation?.sections?.general_info;
      const giContent = gi?.content || gi || {};
      const hasExistingVitals = giContent.temperature || giContent.pulse || giContent.bp || giContent.spo2 || giContent.weight || giContent.height;
      if (hasExistingVitals) return;
      try {
        const { data } = await axios.get(`${API_URL}/api/appointments/vitals`, { params: { appointment_id: apptId }, withCredentials: true });
        if (data && (data.temperature || data.bp || data.pulse || data.spo2 || data.weight || data.height)) {
          saveDraft('general_info', data);
        }
      } catch (e) { /* ignore */ }
    };
    fetchVitals();
  }, [selectedPatient?.appointment_id, consultation?.id]);

  // Extract symptom tags from chief complaints - instant local split + async AI upgrade
  const lastExtractedRef = useRef('');
  useEffect(() => {
    const seeded = consultation?.symptom_tags;
    if (Array.isArray(seeded) && seeded.length > 0 && !lastExtractedRef.current) {
      setSymptomTags(seeded);
    }
    const chief = consultation?.sections?.chief_complaints?.content || '';
    if (!chief || chief === '<p></p>') {
      if (!(Array.isArray(seeded) && seeded.length)) setSymptomTags([]);
      lastExtractedRef.current = '';
      return;
    }
    const clean = stripHtml(chief);
    if (!clean || clean === lastExtractedRef.current) return;

    const fallbackTags = splitSymptoms(clean);
    if (fallbackTags.length > 0) setSymptomTags(fallbackTags);

    const extractSymptoms = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/symptoms/extract`, {
          params: { consultation_id: consultation.id }, withCredentials: true
        });
        if (data.symptoms?.length > 0) setSymptomTags(data.symptoms);
        lastExtractedRef.current = clean;
      } catch (e) {
        lastExtractedRef.current = clean;
      }
    };
    const timeout = setTimeout(extractSymptoms, 2500);
    return () => clearTimeout(timeout);
  }, [consultation?.sections?.chief_complaints?.content, consultation?.id, consultation?.symptom_tags]);

  const getSectionContent = (sectionKey) => {
    if (!consultation?.sections) return '';
    const section = consultation.sections[sectionKey] || {};
    let raw = section.content || section.items || section.data || section.date || '';
    if (!raw && sectionKey === 'chief_complaints') {
      raw = consultation.sections.chief_complaint?.value
        || consultation.sections.chief_complaint?.content
        || '';
    }
    if (typeof raw === 'string' && raw && !raw.trim().startsWith('<')) {
      const esc = raw
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br/>');
      return `<p>${esc}</p>`;
    }
    return raw;
  };

  const getSectionStatus = (key) => {
    return consultation?.sections?.[key]?.status || 'not_started';
  };

  const toggleCollapse = (key) => {
    setCollapsedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const scrollToSection = (key) => {
    sectionRefs.current[key]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveSection(key);
  };

  if (!consultation) {
    return (
      <div className="flex items-center justify-center h-full" data-testid="no-consultation">
        <div className="text-center text-gray-500">
          <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>No active consultation</p>
          <p className="text-sm mt-2">Select a patient to start</p>
        </div>
      </div>
    );
  }

  const completionPercentage = getCompletionPercentage();

  return (
    <div className="flex flex-col h-screen w-full bg-gray-50" data-testid="consultation-dashboard">
      {/* Top Header Bar */}
      <div className="bg-iith-navy border-b border-iith-navyMid px-4 py-2 flex items-center justify-between flex-shrink-0 shadow-sm" data-testid="app-header">
        <div className="flex items-center gap-3">
          {onBack && (
            <Button variant="ghost" size="sm" onClick={async () => { try { await saveNow(); } catch (_) {} onBack(); }} className="h-9 w-9 p-0 text-white hover:bg-white/10 hover:text-white" data-testid="back-to-dashboard">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          )}
          <div className="h-8 w-8 rounded-lg bg-white/10 flex items-center justify-center ring-2 ring-iith-saffron/50">
            <Stethoscope className="h-4 w-4 text-iith-saffron" />
          </div>
          <div>
            <h1 className="text-base font-bold text-white font-heading">IIT Hyderabad Hospital</h1>
            <p className="text-xs text-iith-mute">Dr. {consultation.doctor_name} · Consultation</p>
          </div>
        </div>

        {/* Navigation Pills - scrollable jump-to */}
        <div className="hidden lg:flex items-center gap-1 px-3 overflow-x-auto max-w-[50%]">
          {WORKFLOW_GROUPS.map(g => (
            <button key={g.id} onClick={() => scrollToSection(g.sections[0])}
              className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider whitespace-nowrap transition-all hover:scale-105 ${g.bg} ${g.text} border ${g.color}`}>
              {g.label}
            </button>
          ))}
          <button onClick={() => { setHistoryInitialTab(null); setShowVisitHistory(true); }}
            className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider whitespace-nowrap bg-white/10 text-white/80 border border-white/20 hover:bg-white/15 transition-all">
            <Clock className="h-3 w-3 inline mr-1" />History
          </button>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {/* All-in-one voice dictation — distributes content into every section */}
          <FullVisitDictationButton onConfirm={handleFullVisitApply} />
          <div className="flex items-center gap-1.5">
            <div className="w-20">
              <Progress value={completionPercentage} className="h-1.5" data-testid="progress-bar" />
            </div>
            <span className="text-sm font-bold text-iith-saffron">{completionPercentage}%</span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setShowAIPanel(!showAIPanel)} className="h-8 w-8 p-0 text-white hover:bg-white/10 hover:text-white" data-testid="toggle-ai-panel">
            {showAIPanel ? <PanelRightClose className="h-4 w-4" /> : <PanelRightOpen className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {/* Carry-forward banner — surfaces when the consultation contains
          content that was carried forward from a prior visit (narrative
          sections tagged with "Carried forward from previous visit:" or
          medicines/tests marked from_previous_visit). One-click "Clear
          All" strips just the carry-forward content (the doctor's
          own typing stays intact). "Keep" hides the banner for the
          session. Banner does NOT render on completed/read-only
          consultations — they're locked, so the prompt would be moot. */}
      {!bannerDismissed && !isReadOnly && carryForwardSummary.total > 0 && (
        <div
          className="flex items-center gap-3 px-4 py-2.5 bg-amber-50 border-b border-amber-200 flex-shrink-0"
          data-testid="carry-forward-banner"
        >
          <div className="h-7 w-7 rounded-full bg-amber-200/70 flex items-center justify-center flex-shrink-0">
            <AlertCircle className="h-3.5 w-3.5 text-amber-800" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-900 leading-tight">
              This consultation has data carried forward from past visits
            </p>
            <p className="text-[11px] text-amber-700 leading-tight mt-0.5">
              {carryForwardSummary.narratives.length > 0 && (
                <span>{carryForwardSummary.narratives.length} section{carryForwardSummary.narratives.length !== 1 ? 's' : ''}</span>
              )}
              {carryForwardSummary.narratives.length > 0 && carryForwardSummary.meds.length > 0 && <span> · </span>}
              {carryForwardSummary.meds.length > 0 && (
                <span>{carryForwardSummary.meds.length} medicine{carryForwardSummary.meds.length !== 1 ? 's' : ''}</span>
              )}
              <span className="ml-1 text-amber-600">— review or clear before continuing.</span>
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={handleClearCarryForward}
            disabled={clearingCarryForward}
            className="h-8 text-xs font-semibold bg-white border-amber-300 text-amber-900 hover:bg-amber-100 hover:border-amber-400 gap-1.5"
            data-testid="carry-forward-clear-btn"
          >
            {clearingCarryForward ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <XIcon className="h-3.5 w-3.5" />}
            Clear All
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={dismissBanner}
            className="h-8 text-xs font-semibold text-amber-800 hover:bg-amber-100"
            data-testid="carry-forward-keep-btn"
          >
            Keep
          </Button>
        </div>
      )}

      {/* Main Content: Scrollable Left + Optional AI Right */}
      <div className="flex-1 overflow-hidden flex min-h-0">
        {/* Scrollable Content */}
        <div className="flex-1 overflow-auto" ref={scrollContainerRef}>
          {/* Patient Header Card */}
          <div className="bg-white border-b border-gray-200" data-testid="patient-header">
            <div className="px-6 py-4 bg-gradient-to-r from-iith-mist via-white to-iith-mist">
              <div className="flex items-start gap-4 max-w-full">
                <div className="h-14 w-14 rounded-full bg-iith-navy flex items-center justify-center text-white text-xl font-bold flex-shrink-0 shadow-md ring-2 ring-iith-saffron/40">
                  {consultation.patient_name?.charAt(0) || 'P'}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-xl font-bold text-iith-navy" style={{ fontFamily: 'Manrope' }} data-testid="patient-name-header">
                      {selectedPatient?.salutation ? `${selectedPatient.salutation} ` : ''}
                      {(selectedPatient?.patient_name || consultation.patient_name || '').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}
                    </h2>
                    {selectedPatient?.is_new_hospital && (
                      <Badge className="bg-[#FFF7F3] text-iith-saffron text-xs border border-iith-saffron/30">New Patient</Badge>
                    )}
                    {selectedPatient?.is_new_doctor && !selectedPatient?.is_new_hospital && (
                      <Badge className="bg-iith-mist text-iith-navy text-xs border border-iith-navy/20">New to Doctor</Badge>
                    )}
                    {consultation?.pharmacy_order_number && (
                      <Badge
                        className="bg-green-100 text-green-800 border border-green-300 text-xs gap-1"
                        data-testid="pharmacy-order-chip"
                        title="Prescription posted to pharmacy"
                      >
                        <Pill className="h-3 w-3" /> Rx #{consultation.pharmacy_order_number}
                      </Badge>
                    )}
                    {consultation?.status === 'completed' && !consultation?.pharmacy_order_number && consultation?.pharmacy_post_error && (
                      <Badge
                        className="bg-amber-100 text-amber-800 border border-amber-300 text-xs gap-1"
                        data-testid="pharmacy-order-chip-failed"
                        title={consultation.pharmacy_post_error}
                      >
                        <AlertCircle className="h-3 w-3" /> Pharmacy posting failed
                      </Badge>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-1.5">
                    {patientDetails?.medical_record_number && (
                      <span className="flex items-center gap-1 text-sm font-medium text-gray-700" data-testid="patient-mrn">
                        <Hash className="h-3.5 w-3.5 text-iith-saffron" /> MRN: {patientDetails.medical_record_number}
                      </span>
                    )}
                    {selectedPatient?.appointment_id && (
                      <span className="flex items-center gap-1 text-sm font-medium text-gray-700" data-testid="patient-op-number">
                        <FileText className="h-3.5 w-3.5 text-iith-saffron" /> OP#: {selectedPatient.appointment_id}
                      </span>
                    )}
                    {patientDetails?.phone && (
                      <span className="flex items-center gap-1 text-sm text-gray-500" data-testid="patient-phone">
                        <Phone className="h-3.5 w-3.5" /> {patientDetails.phone}
                      </span>
                    )}
                    {selectedPatient?.time && (
                      <span className="flex items-center gap-1 text-sm text-gray-500" data-testid="patient-appt-time">
                        <Clock className="h-3.5 w-3.5" /> {formatDateTimeIST(selectedPatient.date, selectedPatient.time)}
                      </span>
                    )}
                    {selectedPatient?.specialisation && (
                      <span className="flex items-center gap-1 text-sm text-gray-500" data-testid="patient-specialisation">
                        <Stethoscope className="h-3.5 w-3.5" /> {selectedPatient.specialisation}
                      </span>
                    )}
                    {selectedPatient?.referral_doctor && (
                      <span className="flex items-center gap-1 text-sm font-medium text-iith-saffron" data-testid="patient-referral">
                        <UserCheck className="h-3.5 w-3.5" /> Referred by Dr. {selectedPatient.referral_doctor.name}{selectedPatient.referral_doctor.type ? ` (${selectedPatient.referral_doctor.type})` : ''}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5 mt-2">
                    {selectedPatient?.visit_type && (
                      <Badge variant="outline" className="text-xs font-semibold" data-testid="patient-visit-type">{selectedPatient.visit_type}</Badge>
                    )}
                    {selectedPatient?.status && (
                      <Badge className={`text-xs border ${
                        selectedPatient.status === 'in-visit' ? 'bg-[#FFF7F3] text-iith-saffron border-iith-saffron/30' :
                        selectedPatient.status === 'checked-in' ? 'bg-iith-mist text-iith-navy border-iith-navy/20' :
                        selectedPatient.status === 'confirmed' ? 'bg-green-50 text-green-700 border-green-200' :
                        'bg-gray-50 text-gray-700 border-gray-200'
                      }`} data-testid="patient-status">
                        {selectedPatient.status.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </Badge>
                    )}
                    {selectedPatient?.payment_done !== undefined && (
                      <Badge className={`text-xs border-0 ${selectedPatient.payment_done ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`} data-testid="patient-payment">
                        <CreditCard className="h-3 w-3 mr-0.5" /> {selectedPatient.payment_done ? 'Paid' : 'Unpaid'}
                      </Badge>
                    )}
                    {selectedPatient?.vital_status && (
                      <Badge className={`text-xs border-0 ${selectedPatient.vital_status === 'completed' ? 'bg-sky-100 text-sky-700' : 'bg-yellow-100 text-yellow-700'}`} data-testid="patient-vitals">
                        <Activity className="h-3 w-3 mr-0.5" /> Vitals {selectedPatient.vital_status === 'completed' ? 'Recorded' : 'Pending'}
                      </Badge>
                    )}
                    {selectedPatient?.scheme && (() => {
                      // Color-code the scheme badge by tier so the doctor
                      // sees at a glance which discount tier their patient
                      // belongs to. The actual pharmacy discount % varies
                      // per item (driven by inv.schemeConditions on the
                      // pharmacy posting), but the tier itself is a strong
                      // visual cue when prescribing expensive drugs.
                      const TIERS = {
                        platinum:  { cls: 'bg-purple-100 text-purple-800', icon: '💎', label: 'Top-tier' },
                        diamond:   { cls: 'bg-cyan-100 text-cyan-800',     icon: '💠', label: 'High-tier' },
                        gold:      { cls: 'bg-amber-100 text-amber-800',   icon: '🥇', label: 'Mid-tier' },
                        silver:    { cls: 'bg-slate-200 text-slate-800',   icon: '🥈', label: 'Standard' },
                        bronze:    { cls: 'bg-orange-100 text-orange-800', icon: '🥉', label: 'Entry tier' },
                        insurance: { cls: 'bg-blue-100 text-blue-800',     icon: null, label: 'Third-party billing' },
                        esic:      { cls: 'bg-red-100 text-red-700',       icon: null, label: 'Govt scheme · non-billable for some items' },
                        cmchs:     { cls: 'bg-emerald-100 text-emerald-800', icon: null, label: 'CMCHS govt scheme' },
                        tnnhis:    { cls: 'bg-emerald-100 text-emerald-800', icon: null, label: 'TNNHIS govt scheme' },
                      };
                      const key = selectedPatient.scheme.trim().toLowerCase();
                      const tier = TIERS[key] || { cls: 'bg-teal-100 text-teal-700', icon: null, label: 'Scheme' };
                      return (
                        <Badge
                          className={`${tier.cls} text-xs border-0 font-semibold gap-1`}
                          data-testid="patient-scheme"
                          title={`${tier.label}. Pharmacy discount applied per item from this scheme.`}
                        >
                          <Shield className="h-3 w-3" />
                          {selectedPatient.scheme}
                          {tier.icon && <span className="ml-0.5">{tier.icon}</span>}
                        </Badge>
                      );
                    })()}
                    {recentVisit && (
                      <Badge className="bg-gray-100 text-gray-600 text-xs border-0">
                        <Calendar className="h-3 w-3 mr-0.5" /> Last: Dr. {recentVisit.doctor_name} &middot; {formatDateTimeIST(recentVisit.date)}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {/* Symptom Tags */}
            {symptomTags.length > 0 && (
              <div className="px-6 py-2 bg-[#FFF7F3] border-t border-iith-saffron/20 flex items-center gap-2 flex-wrap" data-testid="symptom-tags-bar">
                <span className="text-[10px] font-bold uppercase tracking-wider text-iith-saffron flex-shrink-0">Symptoms</span>
                {symptomTags.map((tag, i) => (
                  <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-white text-iith-navy border border-iith-saffron/30" data-testid={`symptom-tag-${i}`}>
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Completed visit banner */}
          {consultation?.status === 'completed' && (
            <div
              className={`max-w-full mt-4 mx-4 sm:mx-6 px-4 py-3 rounded-lg border flex items-center justify-between gap-3 flex-wrap ${
                editingCompleted ? 'bg-amber-50 border-amber-300' : 'bg-emerald-50 border-emerald-300'
              }`}
              data-testid="completed-visit-banner"
            >
              <div className="flex items-start gap-2 flex-1 min-w-0">
                {editingCompleted ? (
                  <AlertCircle className="h-5 w-5 text-amber-700 flex-shrink-0 mt-0.5" />
                ) : (
                  <CheckCircle className="h-5 w-5 text-emerald-700 flex-shrink-0 mt-0.5" />
                )}
                <div className="text-sm">
                  {editingCompleted ? (
                    <>
                      <div className="font-semibold text-amber-900">Editing a completed visit</div>
                      <div className="text-amber-800">All changes will be recorded in the audit log.</div>
                    </>
                  ) : (
                    <>
                      <div className="font-semibold text-emerald-900">
                        This visit has been completed
                        {consultation.pharmacy_order_number ? (<>, <span className="font-mono">{consultation.pharmacy_order_number}</span></>) : ''}
                        {consultation.investigations_order_number ? (<>{consultation.pharmacy_order_number ? ' · ' : ', '}<span className="font-mono">{consultation.investigations_order_number}</span></>) : ''}.
                      </div>
                      <div className="text-emerald-800">Sections are read-only. Click Edit to make further changes (audit tracked).</div>
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap flex-shrink-0">
                {consultation?.pharmacy_order_number && (
                  <Button variant="outline" size="sm" onClick={() => setShowPharmacyOrder(true)} data-testid="view-pharmacy-order-btn" className="border-emerald-400 text-emerald-800 hover:bg-emerald-100 gap-1.5">
                    <Pill className="h-4 w-4" /> View Rx Order
                  </Button>
                )}
                {consultation?.investigations_order_number && (
                  <Button variant="outline" size="sm" onClick={() => setShowInvestigationsOrder(true)} data-testid="view-investigations-order-btn" className="border-emerald-400 text-emerald-800 hover:bg-emerald-100 gap-1.5">
                    <TestTube className="h-4 w-4" /> View Investigations
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={() => { setShowVisitHistory(true); setHistoryInitialTab('audit'); }} data-testid="view-audit-log-btn" className="border-emerald-400 text-emerald-800 hover:bg-emerald-100 gap-1.5">
                  <ShieldCheck className="h-4 w-4" /> Audit Log
                </Button>
                {editingCompleted ? (
                  <Button variant="outline" size="sm" onClick={() => setEditingCompleted(false)} data-testid="lock-completed-btn">
                    Done
                  </Button>
                ) : (
                  <Button variant="outline" size="sm" onClick={() => setEditingCompleted(true)} data-testid="edit-completed-btn" className="border-emerald-400 text-emerald-800 hover:bg-emerald-100">
                    Edit (audited)
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Scrollable Sections */}
          <div
            className={`max-w-full px-6 py-6 space-y-5 pb-24 ${isReadOnly ? 'pointer-events-none opacity-80' : ''}`}
            data-testid="sections-container"
            aria-readonly={isReadOnly}
          >
            {WORKFLOW_GROUPS.map(group => (
              <div key={group.id} className={`rounded-xl border-l-4 ${group.color} bg-white shadow-sm overflow-hidden`} data-testid={`group-${group.id}`}>
                {/* Group Header */}
                <div className={`px-4 py-2 ${group.bg} border-b border-gray-100 flex items-center justify-between`}>
                  <h3 className={`text-sm font-bold uppercase tracking-wider ${group.text}`} style={{ fontFamily: 'Manrope' }}>
                    {group.label}
                  </h3>
                  <div className="flex items-center gap-2">
                    {group.sections.map(key => {
                      const status = getSectionStatus(key);
                      return (
                        <TooltipProvider key={key}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button onClick={() => scrollToSection(key)}
                                className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-white/70 hover:bg-white transition-all border border-gray-200/50">
                                {React.createElement(SECTION_ICONS[key], { className: 'h-3 w-3' })}
                                <span className="hidden xl:inline">{TAB_LABELS[key]}</span>
                                <div className={`w-1.5 h-1.5 rounded-full ${status === 'completed' ? 'bg-green-500' : status === 'in_progress' ? 'bg-yellow-400' : 'bg-gray-300'}`} />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom"><p>{TAB_LABELS[key]}</p></TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    })}
                  </div>
                </div>

                {/* Sections within group */}
                <div className="divide-y divide-gray-200">
                  {group.sections.map(key => {
                    const Icon = SECTION_ICONS[key];
                    const isCollapsed = collapsedSections[key];
                    const status = getSectionStatus(key);
                    return (
                      <div key={key} ref={el => sectionRefs.current[key] = el}
                        className="scroll-mt-16" data-testid={`section-${key}`}>
                        {/* Section Header */}
                        <button onClick={() => toggleCollapse(key)}
                          className="w-full px-4 py-2 flex items-center justify-between hover:bg-gray-50/80 transition-colors group bg-white"
                          data-testid={`section-header-${key}`}>
                          <div className="flex items-center gap-2.5">
                            <Icon className={`h-4 w-4 ${group.text}`} />
                            <span className="text-sm font-semibold text-gray-800">{TAB_LABELS[key]}</span>
                            <div className={`w-2 h-2 rounded-full ${status === 'completed' ? 'bg-green-500' : status === 'in_progress' ? 'bg-yellow-400' : 'bg-gray-200'}`} />
                            {status === 'completed' && <span className="text-[10px] text-green-600 font-medium">Done</span>}
                          </div>
                          {isCollapsed ? <ChevronDown className="h-4 w-4 text-gray-400 group-hover:text-gray-600" /> : <ChevronUp className="h-4 w-4 text-gray-400 group-hover:text-gray-600" />}
                        </button>

                        {/* Section Content */}
                        {!isCollapsed && (
                          <div className="px-4 pt-0 pb-3">
                            <SectionContent
                              sectionKey={key}
                              getSectionContent={getSectionContent}
                              saveDraft={saveDraft}
                              consultation={consultation}
                              symptomTags={symptomTags}
                              setShowAdmission={setShowAdmission}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* AI Suggestions Panel */}
        {showAIPanel && (
          <>
            <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setShowAIPanel(false)} />
            <div className="fixed right-0 top-0 bottom-0 z-50 w-80 lg:relative lg:z-auto lg:w-auto lg:inset-auto">
              <AISuggestionsPanel
                activeSection={activeSection}
                onClose={() => setShowAIPanel(false)}
                priorPrescription={lastVisit.prior_prescription || []}
                priorSource={lastVisit.prior_source || null}
              />
            </div>
          </>
        )}
      </div>

      {/* Floating Footer */}
      <footer className="fixed bottom-0 left-0 right-0 z-40 px-4 py-2 border-t border-gray-200 bg-white shadow-[0_-2px_8px_rgba(0,0,0,0.06)]" data-testid="consultation-footer">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          {/* Last-visit pills (left side) */}
          <div className="flex items-center gap-2 flex-wrap" data-testid="last-visit-pills">
            {lastVisit.total_visits > 0 && (
              <span
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-iith-mist border border-iith-navy/15 rounded-full text-xs font-semibold text-iith-navy"
                data-testid="total-visits-chip"
              >
                <Activity className="h-3 w-3 text-iith-saffron" />
                Visits by Patient: {lastVisit.total_visits}
              </span>
            )}
            {lastVisit.by_any && (() => {
              const ba = lastVisit.by_any;
              const hasRx = !!ba.consultation_id;
              const inner = (
                <>
                  <Clock className="h-3 w-3 text-iith-saffron" />
                  <span className="font-semibold text-iith-navy">
                    Last Visited: Dr. {ba.doctor_name || 'Unknown'}
                    {ba.visit_type ? ` · ${ba.visit_type}` : ''} · {formatDateTimeIST(ba.visit_date)}
                  </span>
                </>
              );
              return hasRx ? (
                <button
                  onClick={() => { setPastVisitId(ba.consultation_id); setPastVisitTitle(`Last visit · Dr. ${ba.doctor_name}`); }}
                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#FFF7F3] hover:bg-[#FFEEE6] border border-iith-saffron/25 rounded-full text-xs transition-colors"
                  title="Click to view full prescription details"
                  data-testid="last-visited-btn"
                >
                  {inner}
                </button>
              ) : (
                <span
                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-full text-xs"
                  title="Visit recorded in EMR — no detailed prescription captured here"
                  data-testid="last-visited-btn"
                >
                  <Clock className="h-3 w-3 text-gray-500" />
                  <span className="font-semibold text-gray-700">
                    Last Visited: Dr. {ba.doctor_name || 'Unknown'}
                    {ba.visit_type ? ` · ${ba.visit_type}` : ''} · {formatDateTimeIST(ba.visit_date)}
                  </span>
                </span>
              );
            })()}
          </div>
          <div className="flex items-center gap-2 justify-end flex-wrap">
            <SaveStatusIndicator status={saveStatus} />
            <Button variant="outline" onClick={() => { setHistoryInitialTab(null); setShowVisitHistory(true); }} size="sm" className="gap-1.5 text-sm h-9 text-[#071B34] border-[#D5DEE8]" data-testid="visit-history-btn">
              <History className="h-4 w-4" /> Visit History
            </Button>
            <Button variant="outline" onClick={() => setShowInvestigationHistory(true)} size="sm" className="gap-1.5 text-sm h-9 text-[#071B34] border-[#D5DEE8]" data-testid="investigation-history-btn">
              <TestTube className="h-4 w-4" /> Investigation History
            </Button>
            <Button variant="outline" onClick={() => setShowMedicineHistory(true)} size="sm" className="gap-1.5 text-sm h-9 text-[#071B34] border-[#D5DEE8]" data-testid="medicine-history-btn">
              <Pill className="h-4 w-4" /> Medicine History
            </Button>
            <Button variant="outline" onClick={saveNow} size="sm" className="gap-1.5 text-sm h-9" data-testid="save-btn">
              <Save className="h-4 w-4" /> Save
            </Button>
            <Button variant="outline" onClick={handlePreviewPrintClick} size="sm" className="gap-1.5 text-sm h-9 text-iith-navy border-iith-navy/30 hover:bg-iith-mist" data-testid="preview-print-btn">
              <Printer className="h-4 w-4" /> Preview & Print
            </Button>
            <Button onClick={handleComplete} disabled={completing || consultation?.status === 'completed'} size="sm" className="gap-1.5 bg-iith-saffron hover:bg-[#D94E1C] text-white text-sm h-9" data-testid="complete-btn">
              {completing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
              {consultation?.status === 'completed' ? 'Completed' : (completing ? 'Completing...' : 'Complete Visit')}
            </Button>
          </div>
        </div>
      </footer>

      {/* Past visit drawer (footer pill or visit-history click) */}
      {pastVisitId && (
        <PastVisitDrawer
          consultationId={pastVisitId}
          title={pastVisitTitle}
          onClose={() => { setPastVisitId(null); setPastVisitTitle(''); }}
        />
      )}

      <VisitHistoryDrawer
        open={showVisitHistory}
        onClose={() => setShowVisitHistory(false)}
        patientId={consultation?.patient_id}
        patientName={consultation?.patient_name || selectedPatient?.patient_name}
        activeConsultationId={consultation?.id}
        initialTab={historyInitialTab}
      />
      <InvestigationHistoryDrawer
        open={showInvestigationHistory}
        onClose={() => setShowInvestigationHistory(false)}
        patientId={consultation?.patient_id}
        patientName={consultation?.patient_name || selectedPatient?.patient_name}
      />
      <MedicineHistoryDrawer
        open={showMedicineHistory}
        onClose={() => setShowMedicineHistory(false)}
        patientId={consultation?.patient_id}
        patientName={consultation?.patient_name || selectedPatient?.patient_name}
      />

      {/* Complete Visit Confirmation before Print */}
      {showCompleteConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center" data-testid="complete-confirm-dialog">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowCompleteConfirm(false)} />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 animate-in fade-in zoom-in-95 duration-200">
            <div className="px-6 pt-6 pb-4 border-b border-gray-100">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-iith-mist flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="h-5 w-5 text-iith-saffron" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-base font-bold text-gray-900" style={{ fontFamily: 'Manrope' }}>Complete this visit?</h3>
                  <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                    Completing the visit will finalize the consultation, post the prescription to pharmacy, and push investigations to the lab — all before the print opens.
                  </p>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 flex items-center justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={handleSkipCompleteAndPrint} data-testid="skip-complete-btn" className="text-gray-600">
                No, just print
              </Button>
              <Button size="sm" onClick={handleConfirmCompleteThenPrint} disabled={completing} data-testid="confirm-complete-btn" className="bg-iith-saffron hover:bg-[#D94E1C] gap-1.5">
                {completing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle className="h-3.5 w-3.5" />}
                {completing ? 'Completing...' : 'Yes, complete & print'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Print Preview Modal */}
      {showPrint && (
        <PrescriptionPrint
          consultation={consultation}
          selectedPatient={selectedPatient}
          patientDetails={patientDetails}
          onClose={() => setShowPrint(false)}
          onAfterPrint={() => {
            // Doctor has dispatched the printout — route them back to the
            // dashboard so the next patient is one click away. Save first
            // so any last edits before printing are persisted.
            setShowPrint(false);
            (async () => {
              try { await saveNow(); } catch (_) {}
              if (typeof onBack === 'function') onBack();
            })();
          }}
        />
      )}

      {/* Pharmacy Order Drawer */}
      {showPharmacyOrder && (
        <PharmacyOrderDrawer
          consultationId={consultation?.id}
          orderNumber={consultation?.pharmacy_order_number}
          onClose={() => setShowPharmacyOrder(false)}
        />
      )}

      {/* Investigations Order Drawer */}
      {showInvestigationsOrder && (
        <InvestigationsOrderDrawer
          consultationId={consultation?.id}
          orderNumber={consultation?.investigations_order_number}
          onClose={() => setShowInvestigationsOrder(false)}
        />
      )}

      {/* Modals */}
      <PatientSearchModal open={showPatientSearch} onOpenChange={setShowPatientSearch} />
      <AdmissionRequestModal open={showAdmission} onOpenChange={setShowAdmission} />
      <TemplateSelector open={showTemplates} onOpenChange={setShowTemplates} onSelect={applyTemplate} />
    </div>
  );
}

function PharmacyOrderDrawer({ consultationId, orderNumber, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [order, setOrder] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const { data } = await axios.get(
          `${API_URL}/api/consultations/${consultationId}/pharmacy-order`,
          { withCredentials: true }
        );
        if (!cancelled) setOrder(data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || e.message || 'Failed to fetch pharmacy order');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (consultationId) load();
    return () => { cancelled = true; };
  }, [consultationId]);

  const statusPalette = (c) => {
    switch (c) {
      case 'emerald': return 'bg-emerald-100 text-emerald-800 border-emerald-300';
      case 'amber':   return 'bg-amber-100 text-amber-800 border-amber-300';
      case 'red':     return 'bg-red-100 text-red-800 border-red-300';
      case 'blue':    return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'purple':  return 'bg-purple-100 text-purple-800 border-purple-300';
      default:        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };
  const itemStatusPalette = (s = '') => s === 'PAID'
    ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
    : 'bg-amber-100 text-amber-800 border-amber-300';
  const fmtMoney = (n) => `₹${(Number(n) || 0).toFixed(2)}`;

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-50" onClick={onClose} />
      <div className="fixed top-0 right-0 h-full w-full sm:w-[60vw] sm:min-w-[480px] bg-white z-50 shadow-xl flex flex-col" data-testid="pharmacy-order-drawer">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-emerald-50">
          <div className="flex items-center gap-2">
            <Pill className="h-5 w-5 text-emerald-700" />
            <div>
              <div className="text-sm font-bold text-gray-800" style={{ fontFamily: 'Manrope' }}>Pharmacy Order</div>
              <div className="text-xs text-gray-500 font-mono">{order?.order_id || orderNumber}</div>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="close-pharmacy-order">
            <XIcon className="h-4 w-4 mr-1" /> Close
          </Button>
        </div>
        <div className="flex-1 overflow-auto p-4">
          {loading && (
            <div className="text-center py-16 text-gray-400">
              <Loader2 className="h-8 w-8 mx-auto mb-3 animate-spin" />
              <p className="text-sm font-medium">Fetching order from pharmacy…</p>
            </div>
          )}
          {!loading && error && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="pharmacy-order-error">
              <AlertCircle className="h-4 w-4 inline mr-1" /> {error}
            </div>
          )}
          {!loading && !error && order && (
            <div className="space-y-3" data-testid="pharmacy-order-items">
              {/* Header: Order ID + Status */}
              <div className="rounded-lg border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-3">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-700">Order ID</div>
                    <div className="font-mono text-lg text-emerald-900" data-testid="pharmacy-order-id">{order.order_id}</div>
                  </div>
                  <Badge className={`border ${statusPalette(order.status_color)} text-xs`} data-testid="pharmacy-status-badge">
                    {order.status_label}
                  </Badge>
                </div>
                <div className="flex items-center justify-between text-[11px] text-gray-500">
                  <span>{order.order_type?.toUpperCase()} · {order.patient?.type_number}</span>
                  {order.updated_at && (
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      Updated {formatDateTimeIST(order.updated_at)}
                    </span>
                  )}
                </div>
              </div>

              {/* Patient + Doctor */}
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="rounded-lg border border-gray-200 bg-white p-2.5">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">Patient</div>
                  <div className="font-semibold text-gray-800">{order.patient?.name || '—'}</div>
                  <div className="text-gray-500 text-[11px]">MRN: {order.patient?.mrn} · {order.patient?.age}{order.patient?.gender ? ` · ${order.patient.gender}` : ''}</div>
                  {order.scheme?.name && (
                    <Badge className="bg-blue-100 text-blue-800 text-[10px] border-0 mt-1">{order.scheme.name}</Badge>
                  )}
                </div>
                <div className="rounded-lg border border-gray-200 bg-white p-2.5">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">Doctor</div>
                  <div className="font-semibold text-gray-800">{order.doctor?.name || '—'}</div>
                  <div className="text-gray-500 text-[11px]">{order.doctor?.id}</div>
                </div>
              </div>

              {/* Billing */}
              <div className="rounded-lg border border-gray-200 bg-white p-3" data-testid="pharmacy-billing-summary">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Billing</div>
                  <div className="text-[11px] text-gray-500">
                    {order.payment_summary?.paid ?? 0} / {order.payment_summary?.total_items ?? 0} paid
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-y-1 gap-x-3 text-xs">
                  <div><span className="text-gray-500">Subtotal:</span> <span className="font-medium text-gray-800">{fmtMoney(order.billing?.total_amount)}</span></div>
                  <div><span className="text-gray-500">Tax:</span> <span className="font-medium text-gray-800">{fmtMoney(order.billing?.total_tax)}</span></div>
                  <div><span className="text-gray-500">Discount:</span> <span className="font-medium text-gray-800">{fmtMoney(order.billing?.total_discount)}</span></div>
                  <div><span className="text-gray-500">Advance:</span> <span className="font-medium text-gray-800">{fmtMoney(order.billing?.advance_amount)}</span></div>
                </div>
                <div className="mt-2 pt-2 border-t border-gray-200 flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-600">Grand Total</span>
                  <span className="text-base font-bold text-emerald-800">{fmtMoney(order.billing?.grand_total)}</span>
                </div>
              </div>

              {/* Medicines */}
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1.5 flex items-center gap-1">
                  <Pill className="h-3 w-3" /> Medicines ({order.medicines?.length || 0})
                </div>
                <div className="space-y-2">
                  {(order.medicines || []).map((item, i) => (
                    <div key={i} className="rounded-lg border border-gray-200 bg-white p-3" data-testid={`pharmacy-order-item-${i}`}>
                      <div className="flex items-start justify-between mb-1 gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-sm text-gray-900 truncate">{item.name}</div>
                          {item.composition && (
                            <div className="text-[11px] text-gray-500 italic truncate">{item.composition}</div>
                          )}
                        </div>
                        <Badge className={`text-[10px] border ${itemStatusPalette(item.status)} flex-shrink-0`} data-testid={`rx-status-${i}`}>
                          {item.status || 'PENDING'}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-y-0.5 gap-x-3 text-[11px]">
                        <div><span className="text-gray-500">Dosage:</span> <span className="font-medium text-gray-800">{item.dosage || '—'}</span></div>
                        <div><span className="text-gray-500">Frequency:</span> <span className="font-medium text-gray-800">{item.frequency || '—'}</span></div>
                        <div><span className="text-gray-500">Intake:</span> <span className="font-medium text-gray-800">{item.intake || '—'}</span></div>
                        <div><span className="text-gray-500">Duration:</span> <span className="font-medium text-gray-800">{item.duration || 0} days</span></div>
                        <div><span className="text-gray-500">Quantity:</span> <span className="font-medium text-gray-800">{item.quantity || 0}</span></div>
                        <div><span className="text-gray-500">Unit:</span> <span className="font-medium text-gray-800">{fmtMoney(item.unit_price)}</span></div>
                        {item.batch_number && (
                          <div><span className="text-gray-500">Batch:</span> <span className="font-medium text-gray-800">{item.batch_number}</span></div>
                        )}
                        {item.expiry_date && (
                          <div><span className="text-gray-500">Exp:</span> <span className="font-medium text-gray-800">{(item.expiry_date || '').slice(0, 10)}</span></div>
                        )}
                      </div>
                      <div className="mt-2 pt-2 border-t border-gray-100 flex items-center justify-between text-[11px]">
                        <span className="text-gray-500">Line total</span>
                        <span className="font-semibold text-gray-800">{fmtMoney(item.total)}</span>
                      </div>
                    </div>
                  ))}
                  {(!order.medicines || order.medicines.length === 0) && (
                    <div className="text-center py-6 text-gray-400 text-xs">No medicines on this order</div>
                  )}
                </div>
              </div>

              {/* Labs (if any) */}
              {(order.labs || []).length > 0 && (
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1.5 flex items-center gap-1">
                    <TestTube className="h-3 w-3" /> Lab Tests ({order.labs.length})
                  </div>
                  <div className="space-y-2">
                    {order.labs.map((t, i) => (
                      <div key={i} className="rounded-lg border border-gray-200 bg-white p-2.5 flex items-center justify-between text-xs">
                        <span className="font-medium text-gray-800">{t.name}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">{fmtMoney(t.price)}</span>
                          <Badge className={`text-[10px] border ${itemStatusPalette(t.status)}`}>{t.status}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}


function InvestigationsOrderDrawer({ consultationId, orderNumber, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [order, setOrder] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true); setError('');
      try {
        const { data } = await axios.get(
          `${API_URL}/api/consultations/${consultationId}/investigations-order`,
          { withCredentials: true }
        );
        if (!cancelled) setOrder(data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || e.message || 'Failed to fetch investigations order');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (consultationId) load();
    return () => { cancelled = true; };
  }, [consultationId]);

  const statusPalette = (c) => ({
    emerald: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    amber:   'bg-amber-100 text-amber-800 border-amber-300',
    red:     'bg-red-100 text-red-800 border-red-300',
    blue:    'bg-blue-100 text-blue-800 border-blue-300',
    purple:  'bg-purple-100 text-purple-800 border-purple-300',
  }[c] || 'bg-gray-100 text-gray-800 border-gray-300');
  const fmtMoney = (n) => `₹${(Number(n) || 0).toFixed(2)}`;

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-50" onClick={onClose} />
      <div className="fixed top-0 right-0 h-full w-full sm:w-[60vw] sm:min-w-[480px] bg-white z-50 shadow-xl flex flex-col" data-testid="investigations-order-drawer">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-blue-50">
          <div className="flex items-center gap-2">
            <TestTube className="h-5 w-5 text-blue-700" />
            <div>
              <div className="text-sm font-bold text-gray-800" style={{ fontFamily: 'Manrope' }}>Investigations Order</div>
              <div className="text-xs text-gray-500 font-mono">{order?.order_id || orderNumber}</div>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="close-investigations-order">
            <XIcon className="h-4 w-4 mr-1" /> Close
          </Button>
        </div>
        <div className="flex-1 overflow-auto p-4">
          {loading && (
            <div className="text-center py-16 text-gray-400">
              <Loader2 className="h-8 w-8 mx-auto mb-3 animate-spin" />
              <p className="text-sm font-medium">Fetching investigations order…</p>
            </div>
          )}
          {!loading && error && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="investigations-order-error">
              <AlertCircle className="h-4 w-4 inline mr-1" /> {error}
            </div>
          )}
          {!loading && !error && order && (
            <div className="space-y-3" data-testid="investigations-order-body">
              {/* Header */}
              <div className="rounded-lg border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-white p-3">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="text-[11px] font-bold uppercase tracking-wider text-blue-700">Order ID</div>
                    <div className="font-mono text-lg text-blue-900" data-testid="investigations-order-id">{order.order_id}</div>
                  </div>
                  <Badge className={`border ${statusPalette(order.status_color)} text-xs`} data-testid="investigations-status-badge">
                    {order.status_label}
                  </Badge>
                </div>
                <div className="flex items-center justify-between text-[11px] text-gray-500">
                  <span>{order.patient?.type?.toUpperCase() || 'INVESTIGATION'} · {order.patient?.type_number}</span>
                  {order.updated_at && (
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      Updated {formatDateTimeIST(order.updated_at)}
                    </span>
                  )}
                </div>
              </div>

              {/* Patient + Doctor */}
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="rounded-lg border border-gray-200 bg-white p-2.5">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">Patient</div>
                  <div className="font-semibold text-gray-800">{order.patient?.name || '—'}</div>
                  <div className="text-gray-500 text-[11px]">MRN: {order.patient?.mrn} · {order.patient?.age || '—'}{order.patient?.gender ? ` · ${order.patient.gender}` : ''}</div>
                  {order.scheme?.name && <Badge className="bg-blue-100 text-blue-800 text-[10px] border-0 mt-1">{order.scheme.name}</Badge>}
                </div>
                <div className="rounded-lg border border-gray-200 bg-white p-2.5">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">Doctor</div>
                  <div className="font-semibold text-gray-800">{order.doctor?.name || '—'}</div>
                  <div className="text-gray-500 text-[11px]">{order.doctor?.id}</div>
                </div>
              </div>

              {/* Billing + summary */}
              <div className="rounded-lg border border-gray-200 bg-white p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Billing</div>
                  <div className="text-[11px] text-gray-500">
                    {order.summary?.paid ?? 0} paid · {order.summary?.pending ?? 0} pending
                    {order.summary?.failed ? ` · ${order.summary.failed} failed` : ''}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-y-1 gap-x-3 text-xs">
                  <div><span className="text-gray-500">Subtotal:</span> <span className="font-medium text-gray-800">{fmtMoney(order.billing?.total_amount)}</span></div>
                  <div><span className="text-gray-500">Tax:</span> <span className="font-medium text-gray-800">{fmtMoney(order.billing?.total_tax)}</span></div>
                  <div><span className="text-gray-500">Discount:</span> <span className="font-medium text-gray-800">{fmtMoney(order.billing?.total_discount)}</span></div>
                  <div><span className="text-gray-500">Advance:</span> <span className="font-medium text-gray-800">{fmtMoney(order.billing?.advance_amount)}</span></div>
                </div>
                <div className="mt-2 pt-2 border-t border-gray-200 flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-600">Grand Total</span>
                  <span className="text-base font-bold text-blue-800">{fmtMoney(order.billing?.grand_total)}</span>
                </div>
                {(order.summary?.departments || []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {order.summary.departments.map((d) => (
                      <Badge key={d} className="bg-gray-100 text-gray-700 text-[10px] border-0">{d}</Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Tests */}
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1.5 flex items-center gap-1">
                  <TestTube className="h-3 w-3" /> Tests ({order.tests?.length || 0})
                </div>
                <div className="space-y-2">
                  {(order.tests || []).map((t, i) => (
                    <div key={i} className="rounded-lg border border-gray-200 bg-white p-3" data-testid={`investigations-order-item-${i}`}>
                      <div className="flex items-start justify-between mb-1 gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-sm text-gray-900 truncate">{t.test_name}</div>
                          <div className="text-[11px] text-gray-500 flex flex-wrap items-center gap-1.5">
                            {t.department && <Badge className="bg-blue-50 text-blue-700 text-[10px] border-0">{t.department}</Badge>}
                            {t.modality_type && <span>{t.modality_type}</span>}
                            {t.head_code && <span>· {t.head_code}</span>}
                          </div>
                        </div>
                        <Badge className={`text-[10px] border ${statusPalette(t.status_color)} flex-shrink-0`} data-testid={`inv-status-${i}`}>
                          {t.status}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-3 gap-y-0.5 gap-x-3 text-[11px] mt-1">
                        <div><span className="text-gray-500">Qty:</span> <span className="font-medium text-gray-800">{t.quantity || 1}</span></div>
                        <div><span className="text-gray-500">Unit:</span> <span className="font-medium text-gray-800">{fmtMoney(t.unit_price)}</span></div>
                        <div><span className="text-gray-500">Total:</span> <span className="font-medium text-gray-800">{fmtMoney(t.total)}</span></div>
                      </div>
                      {t.from_date && (
                        <div className="text-[10px] text-gray-400 mt-2 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          Scheduled {formatDateTimeIST(t.from_date)}
                        </div>
                      )}
                    </div>
                  ))}
                  {(!order.tests || order.tests.length === 0) && (
                    <div className="text-center py-6 text-gray-400 text-xs">No tests on this order</div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/* Individual section content renderer */
function SectionContent({ sectionKey, getSectionContent, saveDraft, consultation, symptomTags, setShowAdmission }) {
  if (sectionKey === 'prescription') {
    // Yanthra AI Copilot now lives under Chief Complaints — see the
    // default-branch render below. Prescription stays focused on the
    // prescription table itself.
    return <PrescriptionSection symptomTags={symptomTags} />;
  }
  if (sectionKey === 'investigations') {
    return <InvestigationsSection symptomTags={symptomTags} />;
  }
  if (sectionKey === 'review_date') {
    return <ReviewDatePicker value={getSectionContent('review_date')} onChange={(date) => saveDraft('review_date', date)} />;
  }
  if (sectionKey === 'general_info') {
    // general_info stores its vitals as flat keys (bp/pulse/spo2/...) on
    // the section dict itself — not under .content. Pass the whole
    // section so the GeneralInfoSection's controlled inputs hydrate.
    return <GeneralInfoSection data={consultation?.sections?.general_info || {}} onChange={(data) => saveDraft('general_info', data)} />;
  }
  if (sectionKey === 'admission_request') {
    return (
      <RichTextEditor
        value={getSectionContent('admission_request')}
        onChange={(content) => saveDraft('admission_request', content)}
        placeholder="Enter admission request details..."
        className="h-[120px]"
      />
    );
  }
  if (sectionKey === 'referral') {
    return (
      <ReferralSection
        value={getSectionContent('referral') || null}
        onChange={(data) => saveDraft('referral', data)}
        consultation={consultation}
      />
    );
  }
  // All other text-based sections
  const isLargeSection = ['chief_complaints', 'clinical_examination', 'treatment_plan'].includes(sectionKey);
  // Yanthra AI Suggestions used to sit inside the Prescription section.
  // The clinical workflow is far cleaner when the AI proposes a likely
  // cause + investigations right under the chief complaints, BEFORE the
  // doctor reaches for the prescription pad. That way every section the
  // doctor visits next (examination, investigations, treatment) already
  // has AI context to draw from.
  // Yanthra AI Copilot lives under the Treatment Plan editor — that's
  // where the doctor is actively deciding diagnosis + Rx + investigations,
  // so the suggestions are most actionable there (one-click adopt into
  // the prescription / investigation lanes right beside it).
  const showAICopilot = sectionKey === 'treatment_plan';
  return (
    <>
      <RichTextEditor
        value={getSectionContent(sectionKey)}
        onChange={(content) => saveDraft(sectionKey, content)}
        placeholder={`Enter ${TAB_LABELS[sectionKey]?.toLowerCase() || 'details'}...`}
        className={isLargeSection ? 'h-[140px]' : 'h-[120px]'}
        // Tells the dictation pipeline how to refine the transcript:
        //   chief_complaints     → consolidate patient complaints out of a
        //                           free-flowing patient-doctor conversation
        //   past_history /       → light cleanup: fix drug-name + unit
        //   clinical_examination    mishearings without summarising
        //   treatment_plan
        // Other sections (general_info, prescription) have their own
        // bespoke voice flows so we don't apply refinement there.
        sectionType={sectionKey}
      />
      {showAICopilot && (
        <div className="mt-4" data-testid="yanthra-ai-under-treatment-plan">
          <ClinicalAICopilot />
        </div>
      )}
    </>
  );
}

function SaveStatusIndicator({ status }) {
  const label =
    status === 'saving' ? 'Saving…' :
    status === 'error'  ? 'Save failed' :
    'All changes saved';
  const cls =
    status === 'saving' ? 'bg-blue-50 text-blue-700 border-blue-200' :
    status === 'error'  ? 'bg-red-50 text-red-700 border-red-200' :
    'bg-emerald-50 text-emerald-700 border-emerald-200';
  return (
    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium ${cls}`} data-testid="save-status" title={label}>
      {status === 'saving' && <Loader2 className="h-3 w-3 animate-spin" />}
      {status === 'saved' && <CheckCircle className="h-3 w-3" />}
      {status === 'error' && <AlertCircle className="h-3 w-3" />}
      <span>{label}</span>
    </div>
  );
}

function GeneralInfoSection({ data, onChange }) {
  const [formData, setFormData] = useState(data);
  const isInternalChange = useRef(false);

  useEffect(() => {
    if (isInternalChange.current) { isInternalChange.current = false; return; }
    setFormData(data);
  }, [data]);

  const handleChange = (field, value) => {
    isInternalChange.current = true;
    const newData = { ...formData, [field]: value };
    // Auto-calc BMI when weight or height changes
    if ((field === 'weight' || field === 'height') && newData.weight && newData.height) {
      const w = parseFloat(newData.weight);
      const h = parseFloat(newData.height) / 100;
      if (w > 0 && h > 0) newData.bmi = (w / (h * h)).toFixed(1);
    }
    setFormData(newData);
    onChange(newData);
  };

  const getVitalStatus = (key, value) => {
    if (!value || value === '') return { status: 'empty', label: '', color: '' };
    const v = parseFloat(value);
    if (isNaN(v) && key !== 'bp') return { status: 'empty', label: '', color: '' };

    switch (key) {
      case 'temperature': {
        if (v >= 97.0 && v <= 99.0) return { status: 'normal', label: 'Normal', color: 'text-emerald-600 bg-emerald-50 border-emerald-200' };
        if (v >= 99.1 && v <= 100.3) return { status: 'warning', label: 'Low-grade fever', color: 'text-amber-700 bg-amber-50 border-amber-200' };
        if (v > 100.3) return { status: 'critical', label: v > 103 ? 'High fever' : 'Fever', color: 'text-red-700 bg-red-50 border-red-300' };
        if (v < 97.0 && v > 95) return { status: 'warning', label: 'Low', color: 'text-amber-700 bg-amber-50 border-amber-200' };
        if (v <= 95) return { status: 'critical', label: 'Hypothermia', color: 'text-red-700 bg-red-50 border-red-300' };
        return { status: 'normal', label: '', color: '' };
      }
      case 'pulse': {
        if (v >= 60 && v <= 100) return { status: 'normal', label: 'Normal', color: 'text-emerald-600 bg-emerald-50 border-emerald-200' };
        if (v >= 50 && v < 60) return { status: 'warning', label: 'Bradycardia', color: 'text-amber-700 bg-amber-50 border-amber-200' };
        if (v > 100 && v <= 120) return { status: 'warning', label: 'Mild tachycardia', color: 'text-amber-700 bg-amber-50 border-amber-200' };
        if (v < 50) return { status: 'critical', label: 'Severe bradycardia', color: 'text-red-700 bg-red-50 border-red-300' };
        if (v > 120) return { status: 'critical', label: 'Tachycardia', color: 'text-red-700 bg-red-50 border-red-300' };
        return { status: 'normal', label: '', color: '' };
      }
      case 'bp': {
        const parts = String(value).split('/');
        if (parts.length !== 2) return { status: 'empty', label: '', color: '' };
        const sys = parseFloat(parts[0]), dia = parseFloat(parts[1]);
        if (isNaN(sys) || isNaN(dia)) return { status: 'empty', label: '', color: '' };
        if (sys > 180 || dia > 120) return { status: 'critical', label: 'Hypertensive crisis', color: 'text-red-700 bg-red-50 border-red-300' };
        if (sys >= 140 || dia >= 90) return { status: 'critical', label: 'Stage 2 HTN', color: 'text-red-700 bg-red-50 border-red-300' };
        if ((sys >= 130 && sys < 140) || (dia >= 80 && dia < 90)) return { status: 'warning', label: 'Stage 1 HTN', color: 'text-amber-700 bg-amber-50 border-amber-200' };
        if (sys >= 120 && sys < 130 && dia < 80) return { status: 'warning', label: 'Elevated', color: 'text-amber-700 bg-amber-50 border-amber-200' };
        if (sys < 90 || dia < 60) return { status: 'warning', label: 'Hypotension', color: 'text-amber-700 bg-amber-50 border-amber-200' };
        return { status: 'normal', label: 'Normal', color: 'text-emerald-600 bg-emerald-50 border-emerald-200' };
      }
      case 'spo2': {
        if (v >= 95) return { status: 'normal', label: 'Normal', color: 'text-emerald-600 bg-emerald-50 border-emerald-200' };
        if (v >= 90 && v < 95) return { status: 'warning', label: 'Low', color: 'text-amber-700 bg-amber-50 border-amber-200' };
        return { status: 'critical', label: 'Critical', color: 'text-red-700 bg-red-50 border-red-300' };
      }
      case 'respiration_rate': {
        if (v >= 12 && v <= 20) return { status: 'normal', label: 'Normal', color: 'text-emerald-600 bg-emerald-50 border-emerald-200' };
        if (v >= 8 && v < 12) return { status: 'warning', label: 'Bradypnea', color: 'text-amber-700 bg-amber-50 border-amber-200' };
        if (v > 20 && v <= 25) return { status: 'warning', label: 'Mild tachypnea', color: 'text-amber-700 bg-amber-50 border-amber-200' };
        if (v < 8) return { status: 'critical', label: 'Severe bradypnea', color: 'text-red-700 bg-red-50 border-red-300' };
        return { status: 'critical', label: 'Tachypnea', color: 'text-red-700 bg-red-50 border-red-300' };
      }
      case 'bmi': {
        if (v >= 18.5 && v < 25) return { status: 'normal', label: 'Normal', color: 'text-emerald-600 bg-emerald-50 border-emerald-200' };
        if (v >= 25 && v < 30) return { status: 'warning', label: 'Overweight', color: 'text-amber-700 bg-amber-50 border-amber-200' };
        if (v < 18.5) return { status: 'warning', label: 'Underweight', color: 'text-amber-700 bg-amber-50 border-amber-200' };
        return { status: 'critical', label: v >= 35 ? 'Severe obesity' : 'Obese', color: 'text-red-700 bg-red-50 border-red-300' };
      }
      case 'rbs': {
        // Indian glycemic thresholds: <140 normal (random), 140-199 IGT, ≥200 diabetic range
        if (v >= 70 && v <= 140) return { status: 'normal', label: 'Normal', color: 'text-emerald-600 bg-emerald-50 border-emerald-200' };
        if (v > 140 && v < 200) return { status: 'warning', label: 'Pre-diabetic', color: 'text-amber-700 bg-amber-50 border-amber-200' };
        if (v >= 200) return { status: 'critical', label: 'Hyperglycaemia', color: 'text-red-700 bg-red-50 border-red-300' };
        if (v < 70 && v >= 54) return { status: 'warning', label: 'Hypoglycaemia', color: 'text-amber-700 bg-amber-50 border-amber-200' };
        if (v < 54) return { status: 'critical', label: 'Severe hypoglycaemia', color: 'text-red-700 bg-red-50 border-red-300' };
        return { status: 'empty', label: '', color: '' };
      }
      default: return { status: 'empty', label: '', color: '' };
    }
  };

  const fields = [
    { key: 'temperature', label: 'Temp (°F)', placeholder: '98.6', range: '97–99', unit: '°F' },
    { key: 'pulse', label: 'Pulse', placeholder: '72', range: '60–100', unit: 'bpm' },
    { key: 'bp', label: 'Blood Pressure', placeholder: '120/80', range: '<120/80', unit: 'mmHg' },
    { key: 'spo2', label: 'SpO2', placeholder: '98', range: '95–100', unit: '%' },
    { key: 'respiration_rate', label: 'Resp. Rate', placeholder: '16', range: '12–20', unit: '/min' },
    { key: 'weight', label: 'Weight', placeholder: '70', range: '', unit: 'kg' },
    { key: 'height', label: 'Height', placeholder: '170', range: '', unit: 'cm' },
    { key: 'bmi', label: 'BMI', placeholder: '--', range: '18.5–24.9', unit: 'kg/m²', readOnly: true },
    // RBS — populated from reception triage when present; editable by doctor
    { key: 'rbs', label: 'RBS', placeholder: '110', range: '70–140', unit: 'mg/dL' },
  ];

  // Gather alerts for abnormal vitals
  const alerts = fields
    .map(f => ({ ...f, vital: getVitalStatus(f.key, formData[f.key]) }))
    .filter(f => f.vital.status === 'warning' || f.vital.status === 'critical');

  return (
    <div className="space-y-3" data-testid="general-info-section">
      {/* Voice dictation button — re-dictates and merges vitals */}
      <div className="flex items-center justify-between">
        <p className="text-[11px] text-zinc-500">
          {formData?.bp || formData?.pulse
            ? <>Vitals on file. <span className="text-emerald-700 font-semibold">Edit any value directly.</span></>
            : <>Tap the mic to dictate vitals like “BP 130 over 80, pulse 76”.</>}
        </p>
        <VoiceMicButton
          size="sm"
          mode="vitals"
          label="Dictate vitals"
          data-testid="doc-vitals-mic"
          onParsed={(parsed) => {
            const v = parsed?.vitals || {};
            const merged = { ...formData };
            if (v.bp_systolic && v.bp_diastolic) merged.bp = `${v.bp_systolic}/${v.bp_diastolic}`;
            if (v.pulse != null) merged.pulse = String(v.pulse);
            if (v.spo2 != null) merged.spo2 = String(v.spo2);
            if (v.weight != null) merged.weight = String(v.weight);
            if (v.height != null) merged.height = String(v.height);
            if (v.bmi != null) merged.bmi = String(v.bmi);
            if (v.rbs != null) merged.rbs = String(v.rbs);
            // Temperature: parser emits °C; UI expects °F — convert.
            if (v.temperature != null) {
              const t = Number(v.temperature);
              merged.temperature = t < 50 ? (t * 1.8 + 32).toFixed(1) : String(t);
            }
            isInternalChange.current = true;
            setFormData(merged);
            onChange(merged);
          }}
        />
      </div>
      {/* Vital cards grid */}
      <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
        {fields.map(f => {
          const vital = getVitalStatus(f.key, formData[f.key]);
          const hasValue = formData[f.key] && formData[f.key] !== '';
          const borderClass = vital.status === 'critical' ? 'border-red-300 ring-1 ring-red-200' :
            vital.status === 'warning' ? 'border-amber-300 ring-1 ring-amber-200' :
            vital.status === 'normal' ? 'border-emerald-200' : 'border-gray-200';
          return (
            <div key={f.key} className={`relative flex flex-col rounded-lg border ${borderClass} bg-white overflow-hidden transition-all`}>
              <div className="px-2 pt-1.5 pb-0.5 flex items-center justify-between">
                <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider leading-none">{f.label}</span>
                {hasValue && vital.status !== 'empty' && (
                  <span className={`inline-block w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                    vital.status === 'critical' ? 'bg-red-500 animate-pulse' :
                    vital.status === 'warning' ? 'bg-amber-500' : 'bg-emerald-500'
                  }`} data-testid={`vital-dot-${f.key}`} />
                )}
              </div>
              <div className="px-2 pb-1">
                <div className="flex items-baseline gap-0.5">
                  <input
                    type="text"
                    value={formData[f.key] || ''}
                    onChange={(e) => handleChange(f.key, e.target.value)}
                    readOnly={f.readOnly}
                    className={`w-full text-base font-bold bg-transparent border-0 p-0 focus:ring-0 focus:outline-none ${
                      f.readOnly ? 'text-gray-500 cursor-default' :
                      vital.status === 'critical' ? 'text-red-700' :
                      vital.status === 'warning' ? 'text-amber-700' :
                      hasValue ? 'text-gray-900' : 'text-gray-400'
                    }`}
                    placeholder={f.placeholder}
                    data-testid={`input-${f.key}`}
                  />
                  <span className="text-[9px] text-gray-400 whitespace-nowrap flex-shrink-0">{f.unit}</span>
                </div>
              </div>
              {/* Status tag + range */}
              <div className="px-2 pb-1.5 flex items-center justify-between min-h-[18px]">
                {hasValue && vital.label ? (
                  <span className={`text-[9px] font-semibold px-1 py-0 rounded ${vital.color} border leading-tight`} data-testid={`vital-tag-${f.key}`}>
                    {vital.label}
                  </span>
                ) : f.range ? (
                  <span className="text-[9px] text-gray-300">{f.range}</span>
                ) : <span />}
              </div>
            </div>
          );
        })}
      </div>

      {/* Clinical alerts bar */}
      {alerts.length > 0 && (
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium ${
          alerts.some(a => a.vital.status === 'critical')
            ? 'bg-red-50 border border-red-200 text-red-800'
            : 'bg-amber-50 border border-amber-200 text-amber-800'
        }`} data-testid="vitals-alert-bar">
          <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
          <span>
            {alerts.map((a, i) => (
              <span key={a.key}>
                {i > 0 && <span className="mx-1 opacity-40">|</span>}
                <span className="font-bold">{a.label}:</span> {formData[a.key]} {a.unit} <span className="opacity-70">({a.vital.label})</span>
              </span>
            ))}
          </span>
        </div>
      )}
    </div>
  );
}
